import { gql } from '@apollo/client';
import {
  CoreCheckoutUpdateShippingInfo,
  Input_core_apimessages_CheckoutUpdateShippingAddressRequest as CheckoutUpdateShippingAddressRequest,
  core_apimessages_Error as Error,
} from '@reverbdotcom/commons/src/gql/graphql';
import { useHOCMutation, MutationFunction } from '@reverbdotcom/commons/src/useHOCMutation';
import { ExecutionResult } from 'graphql';
import { useCallback } from 'react';
import { mapAddressToEntry } from '../lib/addressEntryMapping';

export type UpdateCheckoutShippingInfoMutationFn = MutationFunction<CoreCheckoutUpdateShippingInfo.Mutation, CoreCheckoutUpdateShippingInfo.Variables>;

type ShippingInfoInput = Partial<Pick<CheckoutUpdateShippingAddressRequest, 'shippingAddress' | 'guest'>>;

export interface CheckoutShippingForm {
  submit: (input: ShippingInfoInput) => Promise<ExecutionResult<CoreCheckoutUpdateShippingInfo.Mutation>>;
  errors: Error[];
  loading: boolean;
}

export function useCheckoutShippingForm(
  checkoutId: string,
  updateCheckoutShippingInfo: UpdateCheckoutShippingInfoMutationFn,
) {
  const [mutate, { loading, errors }] = useHOCMutation(updateCheckoutShippingInfo);

  const submit = useCallback(async (input: ShippingInfoInput) => {
    const { shippingAddress, guest } = input;

    const result = await mutate({
      variables: {
        input: {
          id: checkoutId,
          shippingAddress: mapAddressToEntry(shippingAddress),
          guest: {
            firstName: guest.firstName,
            lastName: guest.lastName,
            email: guest.email,
          },
        },
      },
      refetchQueries: ['Core_Checkout_Layout'],
      awaitRefetchQueries: true,
    });

    return result;
  }, [checkoutId, mutate]);

  return {
    submit,
    loading,
    errors,
  };
}

export const UPDATE_CHECKOUT_SHIPPING_INFO = gql`
  mutation Core_Checkout_UpdateShippingInfo(
    $input: Input_core_apimessages_CheckoutUpdateShippingAddressRequest
  ) {
    updateCheckoutShippingAddress(input: $input) {
      checkout {
        _id
      }
    }
  }
`;
